import { useState } from 'react';
import { useFormatters, useI18n } from '../../i18n';
import {
  CheckboxField,
  NumberField,
  SelectField,
  type SelectOption,
} from '../../components/Field';
import type {
  AnalysisKind,
  CoCitationKey,
  CountingMethod,
  KeywordSource,
  NormalizationMethod,
  TermLanguage,
} from '../../lib/types';
import {
  DEFAULT_MIN_OCCURRENCES,
  HAS_COUNTING,
  UNITS,
  minOccurrencesHintKey,
  minOccurrencesLabelKey,
  unitHintKey,
  unitMessageKey,
  type ParameterState,
} from './parameters';

/**
 * The parameter block for the chosen analysis.
 *
 * Everything visible is pre-filled with VOSviewer's default and carries a line
 * explaining what it does to the map. The clustering and layout parameters sit
 * behind a disclosure, because changing them without a reason produces a map
 * nobody can compare with anything.
 */
export function ParameterForm({
  analysis,
  state,
  onChange,
  maxItemsCap,
}: {
  analysis: AnalysisKind;
  state: ParameterState;
  onChange: (next: ParameterState) => void;
  maxItemsCap: number;
}) {
  const { t } = useI18n();
  const fmt = useFormatters();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const set = <K extends keyof ParameterState>(key: K, value: ParameterState[K]) =>
    onChange({ ...state, [key]: value });

  const units = UNITS[analysis];

  const countingOptions: readonly SelectOption<CountingMethod>[] = [
    { value: 'full', label: t('counting.full') },
    { value: 'fractional', label: t('counting.fractional') },
  ];

  const keywordSourceOptions: readonly SelectOption<KeywordSource>[] = [
    { value: 'both', label: t('keywordSource.both') },
    { value: 'author', label: t('keywordSource.author') },
    { value: 'index', label: t('keywordSource.index') },
  ];

  const languageOptions: readonly SelectOption<TermLanguage>[] = [
    { value: 'english', label: t('language.english') },
    { value: 'chinese', label: t('language.chinese') },
  ];

  // Match key only. VOSviewer keys bibliographic coupling on a reference's
  // match key but co-citation on the cleaned reference *string*, so the same two
  // works can be one node in one map and two in the other. journalmap uses the
  // match key for both on purpose, and the API has no switch to turn that off --
  // so the second option existed in the UI and nowhere else.
  const cocitationKeyOptions: readonly SelectOption<CoCitationKey>[] = [
    { value: 'matchkey', label: t('cocitationKey.matchkey') },
  ];

  const normalizationOptions: readonly SelectOption<NormalizationMethod>[] = [
    { value: 'association', label: t('normalization.association') },
    { value: 'fractionalization', label: t('normalization.fractionalization') },
    { value: 'linlog', label: t('normalization.linlog') },
    { value: 'none', label: t('normalization.none') },
  ];

  return (
    <div className="stack">
      <div className="grid grid--2">
        {units.length > 0 ? (
          <SelectField
            label={t('param.unit.label')}
            hint={t(unitHintKey(analysis))}
            value={state.unit}
            options={units.map((unit) => ({ value: unit, label: t(unitMessageKey(unit)) }))}
            onChange={(next) => set('unit', next)}
          />
        ) : null}

        {analysis === 'keyword_co_occurrence' ? (
          <SelectField
            label={t('param.keywordSource.label')}
            hint={t('param.keywordSource.hint')}
            value={state.keywordSource}
            options={keywordSourceOptions}
            onChange={(next) => set('keywordSource', next)}
          />
        ) : null}

        {HAS_COUNTING[analysis] ? (
          <SelectField
            label={t('param.counting.label')}
            hint={t('param.counting.hint')}
            value={state.counting}
            options={countingOptions}
            onChange={(next) => set('counting', next)}
          />
        ) : null}

        {analysis === 'co_citation' ? (
          <SelectField
            label={t('param.cocitationKey.label')}
            hint={t('param.cocitationKey.hint')}
            value={state.cocitationKey}
            options={cocitationKeyOptions}
            onChange={(next) => set('cocitationKey', next)}
          />
        ) : null}

        <NumberField
          label={t(minOccurrencesLabelKey(analysis))}
          hint={t(minOccurrencesHintKey(analysis), { default: DEFAULT_MIN_OCCURRENCES[analysis] })}
          value={state.minOccurrences}
          min={1}
          step={1}
          onChange={(next) => set('minOccurrences', next)}
        />

        <NumberField
          label={t('param.minCitations.label')}
          hint={t('param.minCitations.hint')}
          value={state.minCitations}
          min={0}
          step={1}
          onChange={(next) => set('minCitations', next)}
        />

        <NumberField
          label={t('param.maxItems.label')}
          hint={t('param.maxItems.hint', { cap: fmt.int(maxItemsCap) })}
          optional
          placeholder={t('common.unlimited')}
          value={state.maxItems}
          min={1}
          max={maxItemsCap}
          step={1}
          onChange={(next) => set('maxItems', next)}
        />
      </div>

      {analysis === 'terms' ? (
        <div className="grid grid--2">
          <div className="field">
            <span className="field__label">{t('param.fields.label')}</span>
            <CheckboxField
              label={t('fields.title')}
              checked={state.useTitle}
              onChange={(next) => set('useTitle', next)}
            />
            <CheckboxField
              label={t('fields.abstract')}
              hint={t('param.fields.hint')}
              checked={state.useAbstract}
              onChange={(next) => set('useAbstract', next)}
            />
          </div>

          <SelectField
            label={t('param.language.label')}
            hint={t('param.language.hint')}
            value={state.language}
            options={languageOptions}
            onChange={(next) => set('language', next)}
          />

          <NumberField
            label={t('param.relevanceFraction.label')}
            hint={t('param.relevanceFraction.hint')}
            value={state.relevanceFraction}
            min={0}
            max={1}
            step={0.05}
            onChange={(next) => set('relevanceFraction', next)}
          />

          {/* No "number of terms" field. VOSviewer keeps a fraction of the
              ranked terms, and that is what the API exposes (relevance_fraction
              above). There is no n_terms parameter to send, so a control for it
              could only produce a rejected run. */}

          <CheckboxField
            label={t('param.binaryCounting.label')}
            hint={t('param.binaryCounting.hint')}
            checked={state.binaryCounting}
            onChange={(next) => set('binaryCounting', next)}
          />
        </div>
      ) : null}

      <div className="disclosure">
        <button
          type="button"
          className="disclosure__toggle"
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced((open) => !open)}
        >
          {showAdvanced ? t('common.hideAdvanced') : t('common.showAdvanced')}
        </button>

        {showAdvanced ? (
          <div className="disclosure__body stack">
            <p className="field__hint">{t('adv.note')}</p>

            <div className="grid grid--2">
              <SelectField
                label={t('param.normalization.label')}
                hint={t('param.normalization.hint')}
                value={state.normalization}
                options={normalizationOptions}
                onChange={(next) => set('normalization', next)}
              />

              <NumberField
                label={t('param.resolution.label')}
                hint={t('param.resolution.hint')}
                value={state.resolution}
                min={0}
                step={0.05}
                onChange={(next) => set('resolution', next ?? 1)}
              />

              <NumberField
                label={t('param.minClusterSize.label')}
                hint={t('param.minClusterSize.hint')}
                value={state.minClusterSize}
                min={1}
                step={1}
                onChange={(next) => set('minClusterSize', next ?? 1)}
              />

              <NumberField
                label={t('param.clusterRandomStarts.label')}
                hint={t('param.clusterRandomStarts.hint')}
                value={state.clusterRandomStarts}
                min={1}
                step={1}
                onChange={(next) => set('clusterRandomStarts', next ?? 10)}
              />

              <NumberField
                label={t('param.clusterIterations.label')}
                hint={t('param.clusterIterations.hint')}
                value={state.clusterIterations}
                min={1}
                step={1}
                onChange={(next) => set('clusterIterations', next ?? 10)}
              />

              <NumberField
                label={t('param.attraction.label')}
                hint={t('param.attraction.hint')}
                value={state.attraction}
                step={1}
                onChange={(next) => set('attraction', next ?? 2)}
              />

              <NumberField
                label={t('param.repulsion.label')}
                hint={t('param.repulsion.hint')}
                value={state.repulsion}
                step={1}
                onChange={(next) => set('repulsion', next ?? 1)}
              />

              <NumberField
                label={t('param.seed.label')}
                hint={t('param.seed.hint')}
                value={state.seed}
                step={1}
                onChange={(next) => set('seed', next ?? 0)}
              />
            </div>

            <CheckboxField
              label={t('param.mergeSmallClusters.label')}
              hint={t('param.mergeSmallClusters.hint')}
              checked={state.mergeSmallClusters}
              onChange={(next) => set('mergeSmallClusters', next)}
            />

            <CheckboxField
              label={t('param.largestComponent.label')}
              hint={t('param.largestComponent.hint')}
              checked={state.largestComponent}
              onChange={(next) => set('largestComponent', next)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
